/* Parcours interactif élève (bloc 09) : Naïa pose les questions, l'élève répond */
/* en chat, guidage socratique jusqu'à réussite, puis question suivante. */

import { useEffect, useRef, useState } from 'react';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { useParams } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { api } from '../lib/api';
import { streamInteractiveTurn } from '../lib/interactive';
import { ParcoursRecap } from '../components/parcours-recap';
import Markdown from '../components/markdown';
import { Alert } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { composerKeyHandler, isParcoursSubmit } from '../lib/composer-keys';

interface Choice {
  id: string;
  letter: string;
  label: string;
}
interface Question {
  index: number;
  total: number;
  title: string;
  prompt_text: string;
  choices: Choice[];
}
interface Msg {
  role: 'user' | 'assistant';
  text: string;
}
interface State {
  title: string;
  total: number;
  index: number;
  started: boolean;
  completed: boolean;
  current: Question | null;
  transcript: Msg[];
  recap: string | null;
}

const ENONCE_PANEL_ID = 'parcours-enonce';

const msgBase = "max-w-[84%] p-[10px_13px] rounded-[14px] text-[14.5px] leading-[1.5] whitespace-pre-wrap";

export default function InteractiveParcours() {
  const { studentId, assignmentId } = useParams<{ studentId: string; assignmentId: string }>();
  const [state, setState] = useState<State | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [draft, setDraft] = useState('');
  /* La ligne du haut rappelle l'énoncé ; c'est le fil qui le pose. Repliée par
     défaut, elle rend une centaine de pixels à la conversation sur un 360×780. */
  const [promptOpen, setPromptOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!studentId || !assignmentId) return;
    api.parentOrTutor.student.interactiveState
      .query({ studentId, assignmentId })
      .then((s) => {
        const st = s as State | null;
        setState(st);
        setMessages(st?.transcript ?? []);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [studentId, assignmentId]);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const runTurn = async (text: string) => {
    if (!studentId || !assignmentId || busy || state?.completed) return;
    setError(null);
    /* Message élève + bulle tutrice vide qui se remplit au fil du stream. Le tour
       d'ouverture n'a pas de message élève : pas de bulle à droite. */
    setMessages((m) => [...m, ...(text ? [{ role: 'user' as const, text }] : []), { role: 'assistant' as const, text: '' }]);
    setBusy(true);
    try {
      await streamInteractiveTurn(studentId, assignmentId, text, {
        onDelta: (t) =>
          setMessages((m) => {
            const copy = m.slice();
            const last = copy[copy.length - 1];
            if (last && last.role === 'assistant') copy[copy.length - 1] = { ...last, text: last.text + t };
            return copy;
          }),
        onQuestion: (t) => setMessages((m) => [...m, { role: 'assistant', text: t }]),
        onRecap: (t) => setState((s) => (s ? { ...s, recap: t } : s)),
        onDone: (d) => {
          setMessages((m) => {
            const copy = m.slice();
            /* La bulle en attente n'est plus la dernière dès qu'une question a été posée après elle. */
            const pending = copy.findIndex((msg) => msg.role === 'assistant' && !msg.text);
            if (pending !== -1 && d.reply) copy[pending] = { ...copy[pending]!, text: d.reply };
            return copy;
          });
          setState((s) =>
            s
              ? { ...s, started: true, current: d.current, completed: d.completed, index: d.current?.index ?? s.total, recap: d.recap ?? s.recap }
              : s,
          );
        },
        onError: (msg) => setError(msg),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    await runTurn(text);
  };

  const onKey = composerKeyHandler(() => void send(), isParcoursSubmit);

  if (loading) {
    return (
      <div className="min-h-dvh bg-background flex flex-col">
        <HeaderContent width="md" showBurger>{null}</HeaderContent>
        <div className="flex-1 w-full max-w-[760px] mx-auto p-[16px_16px_0] flex flex-col min-h-0">
          <p className="text-muted-foreground text-[13px] text-center my-[10px]">Chargement…</p>
        </div>
      </div>
    );
  }
  if (!state) {
    return (
      <div className="min-h-dvh bg-background flex flex-col">
        <HeaderContent width="md" showBurger>{null}</HeaderContent>
        <div className="flex-1 w-full max-w-[760px] mx-auto p-[16px_16px_0] flex flex-col min-h-0">
          {error && <Alert>{error}</Alert>}
          <p className="text-muted-foreground text-[13px] text-center my-[10px]">Parcours introuvable.</p>
        </div>
      </div>
    );
  }

  const doneCount = state.completed ? state.total : state.index;
  const pct = state.total > 0 ? Math.round((doneCount / state.total) * 100) : 0;

  /*
   * Tant que l'élève n'a pas commencé, ni carte ni champ de saisie : aujourd'hui
   * il arrive dans un parcours déjà en cours, et le premier mot qu'il tape est
   * déjà une tentative notée.
   */
  if (!state.started && !state.completed) {
    return (
      <div className="min-h-dvh bg-background flex flex-col">
        <HeaderContent width="md" showBurger>
          <h1 className={HEADER_TITLE_CLASS}>{state.title}</h1>
        </HeaderContent>
        <div className="flex-1 w-full max-w-[760px] mx-auto p-[16px] flex flex-col min-h-0">
          {error && <Alert>{error}</Alert>}
          <div className="flex-1 flex flex-col justify-center gap-[10px] text-center">
            <p className="text-[13px] font-bold tracking-[0.06em] uppercase text-primary">
              {state.total} question{state.total > 1 ? 's' : ''}
            </p>
            <h2 className="text-[19px] font-bold text-foreground leading-[1.3]">{state.title}</h2>
            <p className="text-[14.5px] leading-[1.5] text-muted-foreground max-w-[42ch] mx-auto">
              Naïa va te poser les questions une par une dans le chat. Réponds comme tu peux : si tu bloques,
              elle t'aide pas à pas, sans te donner la réponse.
            </p>
          </div>
          <div className="sticky bottom-0 [padding:10px_0_calc(env(safe-area-inset-bottom)+10px)] bg-[linear-gradient(var(--naia-page)_40%,transparent)]">
            <button
              type="button"
              className="w-full min-h-[48px] rounded-[14px] border-none bg-primary text-primary-foreground text-[16px] font-bold cursor-pointer transition-transform active:scale-[0.98] active:brightness-90 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => void runTurn('')}
              disabled={busy || state.total === 0}
            >
              {busy ? 'Un instant…' : 'Commencer le parcours'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <HeaderContent
        width="md"
        showBurger
        actions={
          <span className="text-[12px] font-bold tracking-[0.04em] text-muted-foreground whitespace-nowrap">
            {state.completed ? 'Terminé' : `Question ${Math.min(state.index + 1, state.total)} / ${state.total}`}
          </span>
        }
      >
        <h1 className={HEADER_TITLE_CLASS}>{state.title}</h1>
      </HeaderContent>
      <div className="flex-1 w-full max-w-[760px] mx-auto p-[16px_16px_0] flex flex-col min-h-0">
        <div className="h-[6px] rounded-full bg-naia-line overflow-hidden mt-2 mb-[14px]">
          <div className="h-full bg-primary transition-[width] duration-[350ms] ease-in-out" style={{ width: `${pct}%` }} />
        </div>

        {state.current && (
          <div className="bg-card border border-border [border-left:3px_solid_var(--naia-blue)] rounded-card mb-[14px]">
            <button
              type="button"
              className="w-full min-h-[44px] flex items-center gap-[8px] text-left p-[11px_14px] bg-transparent border-none cursor-pointer active:bg-[color-mix(in_srgb,var(--naia-blue)_10%,transparent)]"
              onClick={() => setPromptOpen((o) => !o)}
              aria-expanded={promptOpen}
              aria-controls={ENONCE_PANEL_ID}
            >
              {promptOpen ? (
                <CaretDown size={16} weight="bold" className="shrink-0 text-primary" />
              ) : (
                <CaretRight size={16} weight="bold" className="shrink-0 text-primary" />
              )}
              <span className="text-[13px] font-bold text-foreground truncate">
                Question {state.current.index + 1} / {state.total}
                {state.current.title ? ` — ${state.current.title}` : ''}
              </span>
            </button>
            {promptOpen && (
              <div id={ENONCE_PANEL_ID} className="p-[0_14px_14px_38px]">
                <div className="text-[14.5px] leading-[1.55] text-foreground"><Markdown text={state.current.prompt_text} /></div>
                {state.current.choices.length > 0 && (
                  <ul className="mt-[10px] flex flex-col gap-[6px] list-none p-0">
                    {state.current.choices.map((c) => (
                      <li key={c.id} className="flex gap-[8px] text-[14.5px] leading-[1.45] text-foreground">
                        <span className="font-bold text-primary shrink-0">{c.letter}.</span>
                        <span><Markdown text={c.label} /></span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {error && <Alert>{error}</Alert>}

        <div ref={threadRef} className="flex-1 overflow-y-auto flex flex-col gap-[10px] pb-3">
          {messages.length === 0 && !state.completed && (
            <p className="text-muted-foreground text-[13px] text-center my-[10px]">
              {state.current?.choices.length
                ? "Lis la question ci-dessus et réponds par la lettre de l'option choisie. Naïa t'aidera pas à pas."
                : "Lis la question ci-dessus et propose ta réponse. Naïa t'aidera pas à pas."}
            </p>
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              className={cn(
                msgBase,
                m.role === 'user'
                  ? "self-end bg-primary text-primary-foreground rounded-br-[4px]"
                  : "self-start bg-card border border-border text-foreground rounded-bl-[4px]",
              )}
            >
              <Markdown text={m.text} />
            </div>
          ))}
          {busy && <div className="self-start text-muted-foreground text-[13px] italic p-[6px_4px]">Naïa réfléchit…</div>}
          {state.completed && <ParcoursRecap recap={state.recap} />}
        </div>

        {!state.completed && (
          <div className="sticky bottom-0 flex gap-2 [padding:10px_0_calc(env(safe-area-inset-bottom)+10px)] bg-[linear-gradient(var(--naia-page)_40%,transparent)]">
            <textarea
              className="flex-1 resize-none font-[inherit] text-[15px] leading-[1.4] p-[11px_13px] border border-border rounded-[14px] bg-card text-foreground max-h-[120px]"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              enterKeyHint="send"
              placeholder="Ta réponse…"
              disabled={busy}
            />
            <button
              type="button"
              className="self-end min-w-[48px] min-h-[48px] border-none rounded-[14px] bg-primary text-primary-foreground text-[18px] cursor-pointer transition-transform active:scale-[0.94] active:brightness-90 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => void send()}
              disabled={busy || !draft.trim()}
              aria-label="Envoyer"
            >
              ↑
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
